import { useCallback, useEffect, useMemo, useState } from "react"
import { useLDFlags } from "@shared/integration/launchdarkly"
import { NIG_ID, ANDI_ID, HAVEN_ID, ADMIRAL_ID, RSA_ID } from "../const/policies"
import { useAuth, usePolicyAndFleet } from "../contexts"
import { Permissions } from "../types"

// --------------------------------------------------------------------------------------------------------------------

type UnrestrictedFeatureCapability = `FLEET` | `INSIGHTS` | `SETTINGS` | `BILLING`
type RestrictedFeatureCapability = `CLAIMS` | `REBATES` | `DOCUMENTS` | `MILEAGE`
type FleetFeatureCapability = `ADD_VEHICLES` | `EDIT_COVER_DATES`
type FeatureCapability = UnrestrictedFeatureCapability | RestrictedFeatureCapability | FleetFeatureCapability

export type UsePolicyAccessCapabilities = {
  isRebatePolicy: boolean
  isRebateV2Policy: boolean
  isNotAndiPolicy: boolean
  isAndiPolicy?: boolean
  isHavenPolicy: boolean
  isAdmiralPolicy: boolean
  policyAvailableCapabilities: Array<FeatureCapability>
  policyHasAccessTo: (feature: FeatureCapability) => boolean
  hasPermission: (permission: Permissions | ReadonlyArray<Permissions>) => boolean
}

// --------------------------------------------------------------------------------------------------------------------

const toPermissions = (token: string): ReadonlyArray<Permissions> => {
  const base64Url = token.split(`.`)[1]
  const base64 = base64Url.replace(/-/g, `+`).replace(/_/g, `/`)

  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split(``)
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(``)
  )

  return JSON.parse(jsonPayload).permissions || []
}

// --------------------------------------------------------------------------------------------------------------------

export const usePolicyAccessCapabilities = (): UsePolicyAccessCapabilities => {
  const { policy } = usePolicyAndFleet()
  const { getAuthUser, getToken } = useAuth()
  const { disableVehicleMtas, useCharges } = useLDFlags()

  const [token, setToken] = useState<string | undefined>(undefined)

  useEffect(() => {
    ;(async () => {
      const t = await getToken()
      setToken(t ?? undefined)
    })()
  }, [getToken])

  const permissions = useMemo<ReadonlyArray<Permissions>>(
    () => (token !== undefined ? toPermissions(token) : []),
    [token]
  )

  /**
   * Checks the current users JWT token for desired permission / permissions
   * @returns Boolean
   * @example hasPermission(Permissions.PoliciesAdjust) // true
   * hasPermission([Permissions.PoliciesAdjust, Permissions.PoliciesLapse]) // false
   */
  const hasPermission = useCallback(
    (permission: Permissions | ReadonlyArray<Permissions>): boolean => {
      if (typeof permission === `string`) return permissions.includes(permission)

      return permission.every((p) => permissions.includes(p))
    },
    [permissions]
  )

  const user = useMemo(() => getAuthUser(), [getAuthUser])
  const isRebatePolicy = useMemo(() => policy.productType === `rebates`, [policy.productType])
  const isRebateV2Policy = useMemo(
    () =>
      policy.productType === `rebates` &&
      (policy.meta?.productVersion === `V2.0` ||
        policy.meta?.productVersion === `V2.0-HavenV1.0` ||
        policy.meta?.productVersion === `V2.0-AdmiralV1.0` ||
        policy.meta?.productVersion === `V2.0-NIGV1.0` ||
        policy.meta?.productVersion === `V2.0-RSAV1.0`),
    [policy.productType, policy.meta?.productVersion]
  )
  const isNigIdPolicy = useMemo(
    () => policy.underwriter.id === NIG_ID || policy.underwriter.id === RSA_ID,
    [policy.underwriter.id]
  )
  const isNotNigPolicy = useMemo(() => !isNigIdPolicy, [isNigIdPolicy])
  const isNigRebatesPolicy = useMemo(
    () =>
      (policy.underwriter.id === NIG_ID || policy.underwriter.id === RSA_ID) &&
      (policy.meta?.productVersion === `V2.0-NIGV1.0` || policy.meta?.productVersion === `V2.0-RSAV1.0`),
    [policy.underwriter.id, policy.meta?.productVersion]
  )
  const isHavenPolicy = useMemo(() => policy.underwriter.id === HAVEN_ID, [policy])
  const isAdmiralPolicy = useMemo(() => policy.underwriter.id === ADMIRAL_ID, [policy])
  const isAndiPolicy = useMemo(() => policy.underwriter.id === ANDI_ID, [policy.underwriter.id])
  const isNotAndiPolicy = useMemo(() => !isAndiPolicy, [isAndiPolicy])
  const isConnectivityOnlyPolicy = useMemo(
    () => policy.meta?.productVersion === `V1.0-ConnectivityOnlyV1.0`,
    [policy.meta?.productVersion]
  )

  const policyAvailableCapabilities = useMemo<Array<FeatureCapability>>(() => {
    const generalFeatures: Array<FeatureCapability> = [`FLEET`, `INSIGHTS`, `MILEAGE`, `BILLING`]

    // @NOTE - SETTINGS only has configure TSPS. David advised we hide this entire nav link if we can not adjust the policy
    if (hasPermission(Permissions.PoliciesAdjust)) generalFeatures.push(`SETTINGS`)

    const documentsFeatures: Array<RestrictedFeatureCapability> =
      !isConnectivityOnlyPolicy && (isNotNigPolicy || isNigRebatesPolicy) ? [`DOCUMENTS`] : []
    const rebatesFeatures: Array<RestrictedFeatureCapability> =
      !isConnectivityOnlyPolicy && isRebatePolicy && isNotNigPolicy ? [`REBATES`] : []
    const claimsFeatures: Array<RestrictedFeatureCapability> =
      !isConnectivityOnlyPolicy && isNotNigPolicy ? [`CLAIMS`] : []

    return [...generalFeatures, ...documentsFeatures, ...rebatesFeatures, ...claimsFeatures]
  }, [hasPermission, isConnectivityOnlyPolicy, isNigRebatesPolicy, isNotNigPolicy, isRebatePolicy])

  const canDoMtas = useMemo((): boolean => {
    switch (user?.type) {
      case `distributor`:
        return !disableVehicleMtas
      case `admin`:
        return true
      case `customer`:
        return !(policy.meta?.disableFleetMtas ?? false)
      case `claims_admin`:
        return false
      case `carrier`:
        return false
      default:
        throw new Error(`"${user?.type}" is an unrecognized type of user.`)
    }
  }, [disableVehicleMtas, policy, user])

  const policyHasAccessTo = useCallback(
    (feature: FeatureCapability): boolean => {
      switch (feature) {
        case `FLEET`:
        case `INSIGHTS`:
        case `MILEAGE`:
          return true
        case `BILLING`:
          return useCharges
        case `DOCUMENTS`:
          return !isConnectivityOnlyPolicy && (isNotNigPolicy || isNigRebatesPolicy)
        case `CLAIMS`:
          return !isConnectivityOnlyPolicy && isNotNigPolicy
        case `REBATES`:
          return !isConnectivityOnlyPolicy && isRebatePolicy && isNotNigPolicy
        case `ADD_VEHICLES`:
        case `EDIT_COVER_DATES`:
          return canDoMtas && hasPermission(Permissions.FleetsUpdate)
        default:
          throw new Error(`"${feature}" is an unrecognized policy access capability.`)
      }
    },
    [canDoMtas, hasPermission, isConnectivityOnlyPolicy, isNigRebatesPolicy, isNotNigPolicy, isRebatePolicy, useCharges]
  )

  return {
    isRebatePolicy,
    isRebateV2Policy,
    policyAvailableCapabilities,
    policyHasAccessTo,
    hasPermission,
    isNotAndiPolicy,
    isHavenPolicy,
    isAdmiralPolicy,
    isAndiPolicy,
  }
}
